import React, { useEffect, useState, useRef } from "react";
import "../../styling/game-log-notification.css";
import { formatBoldText } from "../../utils/textFormatting";
import PlayerBadge from "./PlayerBadge";

/**
 * GameLogNotification
 * Displays a game log entry as a notification
 * Fades in, stays for 5 seconds (or 10 seconds if message is > 30 characters), then fades out
 * Shows player avatar if the event was triggered by a player
 * Each notification has its own independent timer
 * Can be dismissed by calling dismiss() method via ref
 */
const GameLogNotification = React.forwardRef(({ message, playerName, playerEmoji, playerColor, onComplete, onFadeStateChange }, ref) => {
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  const onCompleteRef = useRef(onComplete);
  const onFadeStateChangeRef = useRef(onFadeStateChange);
  const hideTimerRef = useRef(null);
  const fadeOutTimerRef = useRef(null);
  const isDismissingRef = useRef(false); // Track dismissing state for ref access
  const animationTimeoutRef = useRef(null);

  // Keep onFadeStateChange ref up to date
  useEffect(() => {
    onFadeStateChangeRef.current = onFadeStateChange;
  }, [onFadeStateChange]);

  // Mark as animated in after fade-in completes (only once)
  useEffect(() => {
    if (!hasAnimatedIn && message) {
      animationTimeoutRef.current = setTimeout(() => {
        setHasAnimatedIn(true);
      }, 400); // Match fade-in animation duration
      
      return () => {
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
      };
    }
  }, [message, hasAnimatedIn]);

  // Expose dismiss method via ref
  React.useImperativeHandle(ref, () => ({
    dismiss: () => {
      if (!isDismissingRef.current) {
        isDismissingRef.current = true;
        setIsFadingOut(true);
        // Notify parent that fade state changed
        if (onFadeStateChangeRef.current) {
          onFadeStateChangeRef.current(true);
        }
        // Clear auto-dismiss timer
        if (fadeOutTimerRef.current) {
          clearTimeout(fadeOutTimerRef.current);
          fadeOutTimerRef.current = null;
        }
        // After fade out animation completes, notify parent to remove
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = setTimeout(() => {
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
        }, 300); // Match fade out animation duration
      }
    },
    isDismissing: () => isDismissingRef.current
  }));

  // Keep onComplete ref up to date without triggering effect re-run
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Determine display duration based on message length
    const displayDuration = message && message.length > 30 ? 12500 : 10000;
    
    // After display duration, fade out
    fadeOutTimerRef.current = setTimeout(() => {
      isDismissingRef.current = true;
      setIsFadingOut(true);
      // Notify parent that fade state changed
      if (onFadeStateChangeRef.current) {
        onFadeStateChangeRef.current(true);
      }
      
      // After fade out animation completes, notify parent to remove
      hideTimerRef.current = setTimeout(() => {
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }, 300); // Match fade out animation duration
    }, displayDuration);
    
    return () => {
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [message, isFadingOut]); // Only depend on message, not onComplete

  if (!message) {
    return null;
  }

  // Check if message is multiline (contains newlines)
  const isMultiline = message.includes('\n');

  return (
    <div 
      className={`game-log-notification ${isFadingOut ? 'fading-out' : hasAnimatedIn ? '' : 'fading-in'}`}
      onClick={(e) => e.stopPropagation()} // Prevent clicks on notification from triggering page click
    >
      <div className="game-log-notification-content">
        {playerName && (playerEmoji || playerColor) && (
          <div style={{ marginRight: '16px', flexShrink: 0 }}>
            <PlayerBadge
              playerName={playerName}
              playerColor={playerColor}
              playerEmoji={playerEmoji}
              size="large"
              showLabel={false}
              className="game-log-notification-avatar"
            />
          </div>
        )}
        <span className={`game-log-notification-message ${isMultiline ? 'multiline' : ''}`}>
          {formatBoldText(message)}
        </span>
      </div>
    </div>
  );
});

GameLogNotification.displayName = 'GameLogNotification';

export default GameLogNotification;

